import { Alert, AlertButton, Modal, NativeModules, Platform, SafeAreaView, Text, TouchableOpacity, View } from "react-native";
import { ColorValue } from "react-native/Libraries/StyleSheet/StyleSheet";
import { Str_Cancel, Str_Confirm, Str_Tip } from "../language/string_res";
import { color_3C3C3C, color_main, white } from "../assets/style/colors";
import { useState, useEffect } from "react";

export type DialogStateProps = {
    title?: string,
    message: string,
    onCancel?: () => void,
    onConfirm?: () => void,
    cancelText?: string,
    confirmText?: string,
    cancelColor?: ColorValue,
    confirmColor?: ColorValue,
    outDismiss?: boolean,
};

// Web 平台弹窗组件
let webDialogContainer: HTMLDivElement | null = null;
let currentDialog: DialogStateProps | null = null;
let dialogResolve: (() => void) | null = null;
let backgroundClickHandler: ((e: MouseEvent) => void) | null = null;

const createWebDialog = (prop: DialogStateProps): Promise<void> => {
    return new Promise((resolve) => {
        if (Platform.OS !== 'web') {
            resolve();
            return;
        }

        currentDialog = prop;
        dialogResolve = resolve;

        // 创建或获取容器
        if (!webDialogContainer) {
            webDialogContainer = document.createElement('div');
            webDialogContainer.id = 'react-native-dialog-container';
            webDialogContainer.style.cssText = `
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                z-index: 10000;
                display: flex;
                align-items: center;
                justify-content: center;
                background-color: rgba(0, 0, 0, 0.5);
            `;
            document.body.appendChild(webDialogContainer);
        } else {
            // 移除之前的事件监听器
            if (backgroundClickHandler && webDialogContainer) {
                webDialogContainer.removeEventListener('click', backgroundClickHandler);
                backgroundClickHandler = null;
            }
        }
        
        // 显示容器
        webDialogContainer.style.display = 'flex';

        // 创建弹窗内容
        const dialog = document.createElement('div');
        dialog.style.cssText = `
            background-color: white;
            border-radius: 20px;
            padding: 28px;
            width: 85%;
            max-width: 500px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        `;

        const title = prop.title || Str_Tip;
        const message = prop.message;
        const cancelText = prop.cancelText || Str_Cancel;
        const confirmText = prop.confirmText || Str_Confirm;
        const hasCancel = prop.onCancel || prop.cancelText;

        dialog.innerHTML = `
            <div style="margin-bottom: 20px;">
                <div style="font-size: 20px; font-weight: 600; color: #1A1A1A; text-align: center; margin-bottom: 12px; letter-spacing: 0.3px;">
                    ${title}
                </div>
                <div style="font-size: 15px; color: #666; text-align: center; line-height: 22px; padding: 0 8px;">
                    ${message}
                </div>
            </div>
            <div style="display: flex; gap: 12px; justify-content: space-between;">
                ${hasCancel ? `
                    <button id="dialog-cancel" style="
                        flex: 1;
                        background-color: #F5F5F5;
                        border: 1px solid #E0E0E0;
                        border-radius: 10px;
                        padding: 16px;
                        font-size: 16px;
                        color: #3C3C3C;
                        font-weight: 500;
                        cursor: pointer;
                    ">${cancelText}</button>
                ` : ''}
                <button id="dialog-confirm" style="
                    flex: 1;
                    background-color: ${color_main};
                    border: none;
                    border-radius: 10px;
                    padding: 16px;
                    font-size: 16px;
                    color: white;
                    font-weight: 600;
                    cursor: pointer;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
                ">${confirmText}</button>
            </div>
        `;

        // 清除之前的弹窗
        while (webDialogContainer.firstChild) {
            webDialogContainer.removeChild(webDialogContainer.firstChild);
        }

        webDialogContainer.appendChild(dialog);

        // 绑定事件
        const confirmBtn = dialog.querySelector('#dialog-confirm');
        const cancelBtn = dialog.querySelector('#dialog-cancel');

        const closeDialog = () => {
            if (webDialogContainer && dialog.parentNode === webDialogContainer) {
                webDialogContainer.removeChild(dialog);
            }
            
            // 移除背景点击事件监听器
            if (backgroundClickHandler && webDialogContainer) {
                webDialogContainer.removeEventListener('click', backgroundClickHandler);
                backgroundClickHandler = null;
            }
            
            // 如果容器没有子元素了，隐藏它
            if (webDialogContainer && webDialogContainer.children.length === 0) {
                webDialogContainer.style.display = 'none';
            }
            
            if (dialogResolve) {
                dialogResolve();
                dialogResolve = null;
            }
            currentDialog = null;
        };

        if (confirmBtn) {
            confirmBtn.addEventListener('click', () => {
                prop.onConfirm?.();
                closeDialog();
            });
        }

        if (cancelBtn) {
            cancelBtn.addEventListener('click', () => {
                prop.onCancel?.();
                closeDialog();
            });
        }

        // 点击背景关闭（如果允许）
        if (prop.outDismiss !== false && webDialogContainer) {
            backgroundClickHandler = (e: any) => {
                if (e.target === webDialogContainer) {
                    prop.onCancel?.();
                    closeDialog();
                }
            };
            webDialogContainer.addEventListener('click', backgroundClickHandler);
        }
    });
};

const showAlert = (prop: DialogStateProps) => {
    if (Platform.OS === 'web') {
        createWebDialog(prop);
        return;
    }

    const { title, message, onCancel, onConfirm, cancelText, confirmText } = prop
    const buttons: AlertButton[] = []
    if (onCancel || cancelText) {
        buttons.push({
            text: cancelText || Str_Cancel,
            onPress: (value) => {
                onCancel?.()
            },
            style: 'destructive'
        })
    }

    buttons.push({
        text: confirmText || Str_Confirm,
        onPress: (value) => {
            onConfirm?.()
        },
        style: 'default'
    })

    Alert.alert(title || Str_Tip, message, buttons)
}


export const showDialog = (msg: DialogStateProps) => {
        showAlert(msg)
};
export const showMsgDialog = (msg: string) => {
    showDialog({
        message: msg
    })
};
export const showYewOrNoDialog = (
    msg: string,
    cancel: string = Str_Cancel,
    confirm: string = Str_Confirm,
    onCancel: () => void = () => { },
    onConfirm: () => void = () => { },
) => {
    showDialog({
        message: msg,
        cancelText: cancel,
        confirmText: confirm,
        onCancel: onCancel,
        onConfirm: onConfirm
    })
};
